import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import Layout from '../components/Layout/Layout'
import {
  createStyles,
  Typography,
  Container,
  makeStyles,
} from '@material-ui/core'
import { scrollToDiv, setFullLocale } from '../utils/utils'
import { getTranslations } from '../i18n'
import { useTranslation } from 'react-i18next'
import { useMatomo } from '@datapunt/matomo-tracker-react'
import FaqQuestion from '../components/Faq/FaqQuestion'
import Button from '../components/Button/Button'
import CMSText, { prismicText } from '../components/Global/CMSText'
import { createClient, getSingleDocument } from '../prismicio'

type Tprops = {
  doc: any
  main?: any
  takeback: any
  inStoreText: any
  basket: any
  accountRes?: any
  banner?: any
}

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      padding: theme.spacing(3, 0),
      backgroundColor: theme.extraColors.greyLight,
    },
    button: {
      margin: theme.spacing(0.5),
      display: 'inline-block',
      [theme.breakpoints.down('md')]: {
        margin: theme.spacing(0.25),
      },
    },
    menu: {
      textAlign: 'center',
      marginTop: theme.spacing(3),
    },
    heroContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: 60,
      [theme.breakpoints.up('sm')]: {
        minHeight: 150,
      },
    },
    heroTitle: {
      textAlign: 'center',
      color: theme.palette.common.black,
      [theme.breakpoints.down('md')]: {
        fontSize: 18,
      },
    },
    container: {
      maxWidth: '700px',
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(3),
    },
    catName: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      textTransform: 'uppercase',
    },
  })
)

const Faq = ({
  doc,
  main,
  inStoreText,
  basket,
  takeback,
  accountRes,
  banner,
}: Tprops) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const { trackPageView } = useMatomo()
  const [isExpanded, setIsExpanded] = useState({ cat: null, question: null })

  useEffect(() => {
    trackPageView({ documentTitle: 'FAQ' })
  }, [])

  const handleExpandClick = (question, cat) => {
    setIsExpanded({ cat: cat, question: question })
  }

  return (
    <>
      <Layout
        mainLogo={main?.data?.logo}
        doc={main}
        t={t}
        inStoreText={inStoreText}
        metaTitle={prismicText(doc?.data?.meta_title_faq, t('texts:faq:title'))}
        metaDescription={doc?.data?.meta_description_faq}
        metaKeywords={doc?.data?.meta_keywords_faq}
        pageType={'faq'}
        takeback={takeback}
        basket={basket}
        account={accountRes}
        faq={doc}
        banner={banner}
      >
        <section className={`faq-page ${classes.root}`}>
          <Container className={`faq-page__hero ${classes.heroContainer}`}>
            <Typography
              variant="h2"
              component="h1"
              className={`faq-page__title ${classes.heroTitle}`}
            >
              <CMSText
                asText
                data={doc?.data?.title}
                defaultText={t('texts:faq:title')}
              />
            </Typography>
            <div className={clsx(classes.menu, 'faq-menu')}>
              {doc?.data?.body?.map((data, index) => {
                return (
                  <span key={index}>
                    <Button
                      key={index}
                      className={`faq-page__anchor ${classes.button}`}
                      onClick={() => scrollToDiv(index)}
                      variant="contained"
                      color="primary"
                      size="small"
                    >
                      {prismicText(data?.primary?.categorie)}
                    </Button>
                  </span>
                )
              })}
            </div>
          </Container>
        </section>
        <section className="faq-wrap">
          <Container className={`faq-content ${classes.container}`}>
            {doc?.data?.body?.map((data, cat) => {
              return (
                <div key={cat}>
                  <Typography
                    variant="h3"
                    component="h2"
                    color="primary"
                    className={`faq-page__cat ${classes.catName}`}
                    id={cat}
                  >
                    {prismicText(data?.primary?.categorie)}
                  </Typography>
                  {data?.items?.map((item, questionId) => {
                    return (
                      <div key={questionId}>
                        <FaqQuestion
                          handleExpandClick={handleExpandClick}
                          isExpanded={
                            isExpanded.question === questionId &&
                            isExpanded.cat === cat
                          }
                          questionId={questionId}
                          cat={cat}
                          close_others={doc?.data?.close_others_on_click}
                          question={item.question}
                          answer={item.response}
                        />
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </Container>
        </section>
      </Layout>
    </>
  )
}

export async function getStaticProps({ locale, previewData }) {
  const client = createClient({ previewData })
  const lang = setFullLocale(locale)

  const faq = await getSingleDocument(client, lang, 'faq')
  const takeback = await getSingleDocument(client, lang, 'take_back')
  const general = await getSingleDocument(client, lang, 'general')
  const inStoreText = await getSingleDocument(client, lang, 'instore')
  const basket = await getSingleDocument(client, lang, 'basket')
  const accountRes = await getSingleDocument(client, lang, 'account')
  const banner = await getSingleDocument(client, lang, 'banner')

  return {
    props: {
      doc: faq,
      takeback,
      inStoreText,
      main: general,
      basket,
      accountRes,
      banner,
      i18nResources: await getTranslations(locale, ['texts']),
    },
    revalidate: 300,
  }
}

export default Faq
